import * as yup from "yup";
import { cprToBirthdate } from "../../foundation/js/inputValidators/cprValidator";

export const getYupValidationSchema = (validators, isValueAnArray, isCheckbox) => {
  let schema = isValueAnArray ? yup.array() : isCheckbox ? yup.boolean() : yup.string();
  validators.forEach((validator) => {
    if (validator.type === "RequiredValidator" && isCheckbox) {
      schema = schema.test("is-checked", validator.model.message, (value) => value === true);
    }

    if (validator.type === "RequiredValidator" && !isValueAnArray && !isCheckbox) {
      schema = schema.required(validator.model.message);
    }

    if (validator.type === "RequiredValidator" && isValueAnArray) {
      schema = schema.min(1, validator.model.message);
    }

    if (validator.type === "RegularExpressionValidator") {
      schema = schema.matches(
        new RegExp(validator.model.jsPattern),
        validator.model.message,
      );
    }

    if (validator.type === "FieldMatchValidator") {
      schema = schema.oneOf(
        [yup.ref(validator.model.matchField)],
        validator.model.message,
      );
    }

    if (validator.type === "EmailFormatValidator") {
      schema = schema.email(validator.model.message || "Ugyldig e-mail format");
    }

    if (validator.type === "EmailDomainValidator") {
      schema = schema.test(
        "valid-email-domain",
        validator.model.message || "Ugyldig e-mail domain",
        (value) => {
          if (!value) return true;
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) return false;

          if (
            !Array.isArray(validator.model.validDomains) ||
            validator.model.validDomains.length === 0
          )
            return true;

          const domain = value.split("@")[1];
          return validator.model.validDomains.includes(domain);
        },
      );
    }

    if (validator.type === "MaxFileSizeValidator") {
      schema = schema.test(
        "valid-total-file-size",
        validator.model.message,
        (files) => {
          if (!files || files.length === 0) return true;
          const totalSize = files.reduce((acc, file) => acc + file.size, 0);
          return totalSize <= validator.model.sizeInBytes;
        },
      );
    }

    if (validator.type === "PhoneCountryCodeValidator") {
      schema = schema.test(
        "valid-phone-country-code",
        validator.model.message || "Invalid phone country code",
        (value) => {
          if (!value) return true;
          const phoneCountryCodeRegex = /^\+\d{1,5}$/;
          return phoneCountryCodeRegex.test(value);
        },
      );
    }

    if (validator.type === "CvrValidator") {
        const pattern = validator.model && validator.model.allowConstructedCvr
            ? /^\d{8,10}$/
            : /^\d{8}$/;
        schema = schema.test(
            "valid-cvr",
            (validator.model && validator.model.message) || "CVR-nummer er ikke gyldigt",
            (value) => {
                if (!value) return true;
                return pattern.test(value);
            },
        );
    }

    if (validator.type === "CprValidator") {
      schema = schema.test(
        "valid-cpr",
        validator.model.message ||
          "CPR-nr. er ikke validt. Det skal bestå af 10 tal ddmmåå-xxxx",
        function (value) {
          const result = cprToBirthdate(value);
          if (result instanceof Date && !isNaN(result.getTime())) {
            return true;
          }
          if (Array.isArray(result) && result.length > 0) {
            return this.createError({
              message: result.join("\n"),
            });
          }
          return false;
        },
      );
    }

    if (validator.type === "CprAgeValidator") {
      schema = schema.test(
        "valid-cpr-age",
        validator.model.message ||
          "CPR-nr. er ikke validt. Det skal bestå af 10 tal ddmmåå-xxxx",
        function (value) {
          const result = cprToBirthdate(value);

          if (!(result instanceof Date) || isNaN(result.getTime())) {
            if (Array.isArray(result) && result.length > 0) {
              return this.createError({
                message: result.join("\n"),
              });
            }
            return false;
          }

          const birthDate = result;

          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ) {
            age--;
          }

          const { minAge, maxAge, tooYoungMessage, tooOldMessage } =
            validator.model;

          if (minAge !== undefined && age < minAge) {
            return this.createError({
              message:
                tooYoungMessage || `Du skal være mindst ${minAge} år gammel`,
            });
          }

          if (maxAge !== undefined && maxAge !== 0 && age > maxAge) {
            return this.createError({
              message: tooOldMessage || `Du må højst være ${maxAge} år gammel`,
            });
          }

          return true;
        },
      );
    }

    if (validator.type === "AllowedExtensionsValidator") {
      schema = schema.test(
        "valid-file-extensions",
        validator.model.message || "Filtype er ikke tilladt",
        (files) => {
          if (!files || files.length === 0) return true;

          const acceptedTypes = validator.model.accept;
          if (!acceptedTypes) return true;

          // Parse the accept string (e.g., ".pdf,.doc,.docx" or "image/*")
          const allowedTypes = acceptedTypes
            .split(",")
            .map((type) => type.trim().toLowerCase());

          return files.every((file) => {
            const fileName = file.name.toLowerCase();
            const fileNameParts = fileName.split(".");
            const fileExtension =
              fileNameParts.length > 1 ? "." + fileNameParts.pop() : "";

            return allowedTypes.some((allowed) => {
              // Handle wildcard MIME types (e.g., "image/*")
              if (allowed.includes("/*")) {
                const mimeCategory = allowed.split("/")[0];
                return file.type.toLowerCase().startsWith(mimeCategory);
              }
              // Handle specific MIME types (e.g., "application/pdf")
              if (allowed.includes("/")) {
                return file.type.toLowerCase() === allowed;
              }
              // Handle file extensions (e.g., ".pdf")
              return fileExtension === allowed;
            });
          });
        },
      );
    }
    // other validation types
  });

  return schema;
};
